import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dragula from "dragula";
import "dragula/dist/dragula.css";
import { eventBus } from "../../core/utils/eventBus";
import { ReactSelect } from "@/components/ui/react-select";
import { Country, State } from "country-state-city";
import {
  ChevronUp,
  PlusCircle,
  Trash2,
  Edit,
  FileText,
  Move,
  Info,
  List as ListIcon,
  Search,
  Package,
  Calculator,
  CheckCircle2,
  Download,
  Printer,
  Mail,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import CustomerModal from "../../components/modals/CustomerModal";
import ProductModal from "../../components/modals/ProductModal";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { isStoreChargingTax } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Spinner } from "@/components/ui/spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { all_routes } from "../../Router/all_routes";
import timezones from "timezones.json";
import DefaultEditor from "react-simple-wysiwyg";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { PageContainer } from "@/components/layout/PageContainer";

const NewSale = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const currentUser = useSelector((state) => state.user_data);
  const currentStore = useSelector((state) => state.current_store);

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  // Fetch payment methods available for sales scope
  const { data: availablePaymentMethods = [] } = useQuery({
    queryKey: ["payment-methods-available", "sales", currentStore?.id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "sales",
          store_id: currentStore?.id || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: hasPermission("payment-method read"),
    staleTime: 5 * 60 * 1000,
  });

  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerLabels, setCustomerLabels] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setCustomerSearchTerm(customerSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  const { data: customers = [], isFetching: isCustomersFetching } = useQuery({
    queryKey: ["customers", customerSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: {
          q: customerSearchTerm || undefined,
          per_page: 15,
        },
      });
      return res.data.data || [];
    },
  });

  useEffect(() => {
    if (customers.length > 0) {
      setCustomerLabels((prev) => {
        const next = { ...prev };
        customers.forEach((c) => {
          let displayName = c.full_name || c.name;
          if (c.customer_type !== "individual" && c.company) {
            displayName = c.company;
          }
          const labels = [displayName, c.phone].filter(Boolean);
          next[String(c.id)] = labels.join(" - ");
        });
        return next;
      });
    }
  }, [customers]);

  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productFetchLoading, setProductFetchLoading] = useState(false);
  const productSearchDebounceRef = useRef(null);
  const { data: products = [], isFetching: productSearchLoading } = useQuery({
    queryKey: ["all-pos-products", productSearchTerm, currentStore?.id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          perPage: 20,
          search: productSearchTerm || undefined,
          store_id: currentStore?.id || undefined,
        },
      });

      return res.data.data || [];
    },
    keepPreviousData: true,
  });

  const handleProductCreated = async (newProduct) => {
    setProductFetchLoading(true);
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(newProduct.id),
      );
      const product = res.data?.data;
      applyProductToState(
        product || { ...newProduct, batches: [], serial_numbers: [] },
      );
    } catch {
      applyProductToState({
        ...newProduct,
        batches: newProduct.batches || [],
        serial_numbers: newProduct.serial_numbers || [],
      });
    } finally {
      setProductFetchLoading(false);
    }
    setShowItemModal(true);
  };

  const createSaleMutation = useMutation({
    mutationFn: async (salePayload) => {
      const saleRes = await apiClient.post(API_ENDPOINTS.SALES.SALES, salePayload);
      return saleRes.data.data;
    },
    onSuccess: () => {
      toast.success("Sale created successfully");
      queryClient.invalidateQueries({ queryKey: ["sales"] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create sale");
    },
  });

  const generatePDFMutation = useMutation({
    mutationFn: async (sale) => {
      setCurrentSaleData(sale);
      setPdfModalOpen(true);

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_INVOICE(sale.id),
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) throw new Error("Failed to generate PDF");

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      setPdfType("invoice");
      return URL.createObjectURL(pdfBlob);
    },
    onSuccess: (blobUrl) => {
      setPdfBlobUrl(blobUrl);
    },
    onError: (error) => {
      setPdfBlobUrl(null);
      toast.error(
        error.response?.data?.message || "Failed to generate PDF report",
      );
    },
  });

  const generateReceiptMutation = useMutation({
    mutationFn: async (sale) => {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_RECEIPT(sale.id),
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200)
        throw new Error("Failed to generate Thermal Receipt");

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      return URL.createObjectURL(pdfBlob);
    },
    onSuccess: (blobUrl) => {
      const printWindow = window.open(blobUrl, "_blank");
      printWindow.onload = () => {
        printWindow.print();
      };
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to generate Thermal Receipt",
      );
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async (sale) => {
      const email = sale?.customer?.email;
      if (!email || email === "" || email === "-" || !email.includes("@")) {
        throw new Error("Customer has no valid email address");
      }

      await apiClient.post(
        API_ENDPOINTS.SALES.SEND_INVOICE(sale.id),
        {},
      );
    },
    onSuccess: () => {
      toast.success("Invoice email sent successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to send invoice email",
      );
    },
  });

  const saveSaleItemsMutation = useMutation({
    mutationFn: async (sale) => {
      const itemsPayload = [];

      saleItems.forEach((item) => {
        if (item.serial_numbers && item.serial_numbers.length > 0) {
          const totalQuantity = item.quantity || item.serial_numbers.length;

          item.serial_numbers.forEach((sn) => {
            const serialNumber = typeof sn === "string" ? sn : sn.serial_number;
            const inventorySerialId = typeof sn === "string" ? null : sn.id;

            const unitItem = {
              ...item,
              quantity: 1,
              discount_value:
                item.discount_type === "fixed"
                  ? Number(item.discount_value || 0) / totalQuantity
                  : item.discount_value,
            };

            const { discountAmount, taxAmount, lineTotal } =
              calculateItemTotal(unitItem);

            let product = null;
            try {
              product = item.product_data
                ? typeof item.product_data === "string"
                  ? JSON.parse(item.product_data)
                  : item.product_data
                : null;
            } catch {
              product = null;
            }

            const taxRate = parseFloat(currentStore?.tax_percentage) || 0;

            itemsPayload.push({
              sale_id: sale.id,
              product_id: item.product_id,
              store_id: item.store_id || currentStore?.id,
              quantity: 1,
              unit_price: Number(item.unit_price || 0),
              original_price: Number(item.original_price || 0),
              line_total: lineTotal,
              discount_amount: discountAmount,
              discount_type:
                item.discount_type === "percentage" ||
                  item.discount_type === "fixed"
                  ? item.discount_type
                  : null,
              discount_value: unitItem.discount_value || 0,
              tax_amount: taxAmount,
              tax_class: item.tax_id || (product ? product.tax_id : null),
              tax_percentage: taxRate,
              notes: item.notes || null,
              product_data: item.product_data,
              custom_fields: item.custom_fields || null,
              inventory_serial_id: inventorySerialId,
              serial_number: serialNumber,
              inventory_batch_id: item.inventory_batch_id || null,
              warranty_start_date: item.warranty_start_date || null,
              warranty_end_date: item.warranty_end_date || null,
              warranty_notes: item.warranty_notes || null,
            });
          });
        } else {
          const product = item.product_data
            ? typeof item.product_data === "string"
              ? JSON.parse(item.product_data)
              : item.product_data
            : null;

          const taxRate = parseFloat(currentStore?.tax_percentage) || 0;

          itemsPayload.push({
            ...item,
            sale_id: sale.id,
            discount_type:
              item.discount_type === "percentage" ||
                item.discount_type === "fixed"
                ? item.discount_type
                : null,
            tax_class: item.tax_id || (product ? product.tax_id : null),
            tax_percentage: taxRate,
            serial_number: null,
            inventory_serial_id: null,
            warranty_start_date: item.warranty_start_date || null,
            warranty_end_date: item.warranty_end_date || null,
            warranty_notes: item.warranty_notes || null,
          });
        }
      });

      await apiClient.post(
        API_ENDPOINTS.SALES.SALE_ITEM_BULK,
        { items: itemsPayload },
      );
      return sale;
    },
    onError: (error) => {
      console.error("Failed to save sale items:", error);
      toast.error(error.response?.data?.message || "Failed to save sale items");
    },
  });

  const createPaymentRecordMutation = useMutation({
    mutationFn: async ({ sale_id, amount, payment_method }) => {
      const paymentPayload = {
        sale_id,
        amount: parseFloat(amount),
        payment_method: payment_method || (availablePaymentMethods.length > 0 ? (availablePaymentMethods[0].id || availablePaymentMethods[0].slug || availablePaymentMethods[0]) : "cash"),
        transaction_id: `TXN-${Date.now()}`,
        payment_date: getCurrentDateInStoreTimezone(),
        payment_status: "completed",
        notes: null,
      };

      await apiClient.post(API_ENDPOINTS.SALES.PAYMENTS, paymentPayload);
      return { sale_id };
    },
    onError: (error) => {
      console.error("Failed to create payment record", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to record payment for this sale",
      );
    },
  });

  const [recordPayment, setRecordPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);

  const [saleData, setSaleData] = useState({
    customer_id: null,
    store_id: currentStore?.id || null,
    user_id: currentUser?.id || null,
    discount_type: null,
    discount_value: 0,
    payment_status: "pending",
    payment_method: null,
    notes: null,
    custom_fields: null,
    item_count: 0,
  });

  const [saleItems, setSaleItems] = useState([]);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  const dragContainerRef = useRef(null);

  useEffect(() => {
    if (dragContainerRef.current) {
      const drake = dragula([dragContainerRef.current], {
        moves: (el, container, handle) => {
          return (
            handle.classList.contains("drag-handle") ||
            handle.closest(".drag-handle")
          );
        },
      });

      drake.on("drop", (_, target) => {
        const rows = Array.from(target.children);
        const newOrder = rows.map((row) =>
          parseInt(row.getAttribute("data-index")),
        );

        setSaleItems((prevItems) => {
          return newOrder.map((index) => prevItems[index]);
        });

        drake.cancel(true);
      });

      return () => drake.destroy();
    }
  }, []);

  useEffect(() => {
    const unsub = eventBus.subscribe("sale:created", (payload) => {
      if (payload.recordPayment && parseFloat(payload.amount) > 0) {
        createPaymentRecordMutation.mutate({
          sale_id: payload.sale.id,
          amount: payload.amount,
          payment_method: payload.payment_method,
        });
      }
    });
    return unsub;
  }, [createPaymentRecordMutation]);

  const prevStoreRef = useRef(currentStore?.id);

  useEffect(() => {
    if (prevStoreRef.current && prevStoreRef.current !== currentStore?.id) {
      // Store changed! Clear the cart/pos everything like it's preparing for new sale
      setSaleItems([]);
      setSaleData({
        customer_id: null,
        store_id: currentStore?.id || null,
        user_id: currentUser?.id || null,
        discount_type: null,
        discount_value: 0,
        payment_status: "pending",
        payment_method: null,
        notes: null,
        custom_fields: null,
        item_count: 0,
      });
      setRecordPayment(false);
      setPaymentAmount(0);
      setCustomerSearchInput("");
      setCustomerSearchTerm("");
    }
    prevStoreRef.current = currentStore?.id;
  }, [currentStore?.id, currentUser?.id]);

  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [currentItem, setCurrentItem] = useState({
    product_id: null,
    store_id: currentStore?.id || null,
    quantity: 1,
    unit_price: 0,
    original_price: 0,
    line_total: 0,
    discount_amount: 0,
    discount_type: null,
    discount_value: 0,
    tax_amount: 0,
    tax_id: null,
    notes: null,
    product_data: null,
    inventory_batch_id: null,
    serial_numbers: [],
    custom_fields: null,
    warranty_start_date: null,
    warranty_end_date: null,
    warranty_notes: null,
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfType, setPdfType] = useState("invoice");
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentSaleData, setCurrentSaleData] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const paymentStatusOptions = [
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
    { value: "refunded", label: "Refunded" },
    { value: "cancelled", label: "Cancelled" },
    { value: "on-hold", label: "On Hold" },
    { value: "partially-refunded", label: "Partially Refunded" },
    { value: "partially-paid", label: "Partially Paid" },
  ];

  const paymentMethodOptions = availablePaymentMethods.map((pm) => ({
    value: pm.id || pm.slug || pm,
    label: pm.name || pm,
  }));

  const discountTypeOptions = [
    { value: "percentage", label: "Percentage (%)" },
    { value: "fixed", label: "Fixed Amount" },
  ];

  const getCurrentDateInStoreTimezone = () => {
    if (!currentStore?.time_zone) {
      return new Date().toISOString();
    }

    const [timezoneName] = currentStore.time_zone.split("-");

    const timezoneData = timezones.find((tz) => tz.value === timezoneName);

    if (!timezoneData) {
      return new Date().toISOString();
    }

    const now = new Date();
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;

    const storeTime = new Date(utcTime + timezoneData.offset * 3600000);

    return storeTime.toISOString();
  };

  const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

  const calculateItemTotal = (item) => {
    const basePrice = round2(
      Number(item.unit_price || 0) * Number(item.quantity || 1),
    );
    let discountAmount = 0;

    if (item.discount_type === "percentage" && item.discount_value > 0) {
      discountAmount = round2((basePrice * Number(item.discount_value)) / 100);
    } else if (item.discount_type === "fixed" && item.discount_value > 0) {
      discountAmount = round2(Number(item.discount_value));
    }

    const { taxAmount, lineTotal } = calculateTaxValues(item);

    return {
      discountAmount: round2(discountAmount),
      taxAmount: round2(taxAmount),
      lineTotal: round2(lineTotal),
    };
  };

  const calculateSaleTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    saleItems.forEach((item) => {
      const itemLineTotal = parseFloat(item.line_total || 0);
      const itemTaxAmount = parseFloat(item.tax_amount || 0);

      subtotal += itemLineTotal;
      totalTax += itemTaxAmount;
      totalDiscount += parseFloat(item.discount_amount || 0);
    });

    subtotal = round2(subtotal);
    totalTax = round2(totalTax);
    totalDiscount = round2(totalDiscount);

    let saleDiscount = 0;
    if (
      saleData.discount_type === "percentage" &&
      saleData.discount_value > 0
    ) {
      saleDiscount = round2((subtotal * saleData.discount_value) / 100);
    } else if (
      saleData.discount_type === "fixed" &&
      saleData.discount_value > 0
    ) {
      saleDiscount = round2(saleData.discount_value);
    }

    const total = round2(subtotal + totalTax - saleDiscount);

    return {
      subtotal,
      totalTax,
      totalDiscount,
      saleDiscount,
      total,
    };
  };

  const calculateTaxValues = (item) => {
    const basePrice = round2(
      Number(item.unit_price || 0) * Number(item.quantity || 1),
    );
    let discountAmount = 0;

    if (item.discount_type === "percentage" && item.discount_value > 0) {
      discountAmount = round2((basePrice * Number(item.discount_value)) / 100);
    } else if (item.discount_type === "fixed" && item.discount_value > 0) {
      discountAmount = round2(Number(item.discount_value));
    }

    const discountedPrice = round2(basePrice - discountAmount);

    const product = item.product_data
      ? typeof item.product_data === "string"
        ? JSON.parse(item.product_data)
        : item.product_data
      : products.find((p) => p.id === item.product_id);

    if (!isStoreChargingTax(currentStore) || !product?.is_taxable) {
      return { taxAmount: 0, lineTotal: discountedPrice };
    }

    const taxRate = parseFloat(currentStore.tax_percentage) || 0;
    const taxMethod = product.tax_method || "exclusive";

    if (taxMethod === "inclusive") {
      return { taxAmount: 0, lineTotal: discountedPrice };
    } else {
      const taxAmount = round2((discountedPrice * taxRate) / 100);
      return { taxAmount, lineTotal: discountedPrice };
    }
  };

  const calculateTaxAmount = (item) => {
    return calculateTaxValues(item).taxAmount;
  };

  const applyProductToState = (product) => {
    const batches = product.batches || [];
    let serials = product.serial_numbers || [];

    if (product.is_serialized && saleItems) {
      const addedSerials = saleItems.reduce((acc, item) => {
        if (item.product_id === product.id) {
          let itemSerials = [];
          if (
            Array.isArray(item.serial_numbers) &&
            item.serial_numbers.length > 0
          ) {
            itemSerials = item.serial_numbers;
          } else if (
            typeof item.serial_numbers === "string" &&
            item.serial_numbers !== "null"
          ) {
            try {
              itemSerials = JSON.parse(item.serial_numbers || "[]");
            } catch {
              itemSerials = [];
            }
          } else if (item.inventory_serial?.serial_number) {
            itemSerials = [item.inventory_serial.serial_number];
          }

          if (Array.isArray(itemSerials)) {
            acc.push(
              ...itemSerials.map((sn) =>
                typeof sn === "string" ? sn : sn.serial_number,
              ),
            );
          }
        }
        return acc;
      }, []);

      serials = serials.filter(
        (sn) => !addedSerials.includes(sn.serial_number),
      );
    }

    setAvailableBatches(batches);
    setAvailableSerials(serials);
    setSelectedProduct(product);

    const sellPrice = parseFloat(product.sell_price) || 0;

    let warrantyStart = currentItem.warranty_start_date;
    let warrantyEnd = currentItem.warranty_end_date;

    // Only calculate defaults if this is a newly selected product (not an edit of the same product)
    if (currentItem.product_id !== product.id) {
      if (
        product.warranty_timeframe &&
        product.warranty_timeframe !== "No Warranty" &&
        product.warranty > 0
      ) {
        const now = new Date();
        warrantyStart = now.toISOString().split("T")[0];
        const endDate = new Date(now);
        if (product.warranty_timeframe === "Days")
          endDate.setDate(endDate.getDate() + parseInt(product.warranty));
        else if (product.warranty_timeframe === "Months")
          endDate.setMonth(endDate.getMonth() + parseInt(product.warranty));
        else if (product.warranty_timeframe === "Years")
          endDate.setFullYear(
            endDate.getFullYear() + parseInt(product.warranty),
          );
        else if (product.warranty_timeframe === "Lifetime")
          endDate.setFullYear(
            endDate.getFullYear() + parseInt(product.warranty),
          );
        warrantyEnd = endDate.toISOString().split("T")[0];
      } else {
        warrantyStart = null;
        warrantyEnd = null;
      }
    }

    const newItem = {
      ...currentItem,
      product_id: product.id,
      unit_price: sellPrice,
      original_price: sellPrice,
      tax_id: product.tax_id,
      inventory_batch_id: batches.length > 0 ? batches[0].id : null,
      serial_numbers: [],
      warranty_start_date: warrantyStart,
      warranty_end_date: warrantyEnd,
      product_data: JSON.stringify({
        id: product.id,
        name: product.name,
        sku: product.sku,
        barcode: product.barcode,
        sell_price: product.sell_price,
        sell_price_min: product.sell_price_min,
        sell_price_max: product.sell_price_max,
        brand:
          typeof product.brand === "string"
            ? product.brand
            : product.brand?.name,
        model:
          typeof product.model === "string"
            ? product.model
            : product.model?.name,
        unit:
          typeof product.unit === "string" ? product.unit : product.unit?.name,
        batches: product.batches,
        tracking_type: product.tracking_type,
        is_serialized: product.is_serialized,
        is_taxable: product.is_taxable,
        tax_method: product.tax_method,
      }),
    };

    const { taxAmount, lineTotal } = calculateTaxValues(newItem);
    setCurrentItem({
      ...newItem,
      tax_amount: taxAmount,
      line_total: lineTotal,
    });
  };

  const handleProductSelect = (selectedOption, productData = null) => {
    if (!selectedOption) {
      setSelectedProduct(null);
      setCurrentItem((prev) => ({
        ...prev,
        product_id: null,
        unit_price: 0,
        original_price: 0,
        product_data: null,
        inventory_batch_id: null,
        serial_numbers: [],
      }));
      setAvailableSerials([]);
      setAvailableBatches([]);
      return;
    }

    const product =
      productData ||
      selectedOption?.product ||
      products.find((p) => p.id === selectedOption.value);

    if (product) {
      applyProductToState(product);
    }
  };

  const handleProductSearch = (inputValue) => {
    if (productSearchDebounceRef.current) {
      clearTimeout(productSearchDebounceRef.current);
    }
    productSearchDebounceRef.current = setTimeout(() => {
      if (inputValue.length >= 3) {
        setProductSearchTerm(inputValue);
      } else if (inputValue.length === 0) {
        setProductSearchTerm("");
      }
    }, 400);
  };

  const validateItemDiscount = () => {
    if (!selectedProduct) return true;

    const basePrice = currentItem.unit_price * currentItem.quantity;
    let discountAmount = 0;

    if (
      currentItem.discount_type === "percentage" &&
      currentItem.discount_value > 0
    ) {
      discountAmount = (basePrice * currentItem.discount_value) / 100;
    } else if (
      currentItem.discount_type === "fixed" &&
      currentItem.discount_value > 0
    ) {
      discountAmount = currentItem.discount_value;
    }

    const finalPrice = (basePrice - discountAmount) / currentItem.quantity;

    if (
      selectedProduct.sell_price_min &&
      finalPrice < selectedProduct.sell_price_min
    ) {
      toast.error(
        `Price after discount (${currencySymbol}${finalPrice.toFixed(
          2,
        )}) cannot be less than minimum sell price (${currencySymbol}${selectedProduct.sell_price_min
        })`,
      );
      return false;
    }

    if (
      selectedProduct.sell_price_max &&
      finalPrice > selectedProduct.sell_price_max
    ) {
      toast.error(
        `Price after discount (${currencySymbol}${finalPrice.toFixed(
          2,
        )}) cannot exceed maximum sell price (${currencySymbol}${selectedProduct.sell_price_max
        })`,
      );
      return false;
    }

    return true;
  };

  const handleAddItem = () => {
    if (!currentItem.product_id) {
      toast.error("Please select a product");
      return;
    }

    if (currentItem.quantity < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }

    if (!validateItemDiscount()) {
      return;
    }

    if (
      selectedProduct?.is_serialized &&
      currentItem.serial_numbers.length === 0
    ) {
      toast.error("Please select at least one serial number");
      return;
    }

    if (
      selectedProduct?.tracking_type === "standard" &&
      !currentItem.inventory_batch_id
    ) {
      toast.error("Please select a batch");
      return;
    }

    const { discountAmount, taxAmount, lineTotal } =
      calculateItemTotal(currentItem);
    const itemToAdd = {
      ...currentItem,
      discount_amount: discountAmount,
      tax_amount: taxAmount,
      line_total: lineTotal,
    };

    if (editingItemIndex !== null) {
      const updatedItems = [...saleItems];
      updatedItems[editingItemIndex] = itemToAdd;
      setSaleItems(updatedItems);
      setEditingItemIndex(null);
    } else {
      setSaleItems([...saleItems, itemToAdd]);
    }

    setShowItemModal(false);
    resetCurrentItem();
  };

  const resetCurrentItem = () => {
    setCurrentItem({
      product_id: null,
      store_id: currentStore?.id || null,
      quantity: 1,
      unit_price: 0,
      original_price: 0,
      line_total: 0,
      discount_amount: 0,
      discount_type: null,
      discount_value: 0,
      tax_amount: 0,
      tax_id: null,
      notes: null,
      product_data: null,
      inventory_batch_id: null,
      serial_numbers: [],
      custom_fields: null,
      warranty_start_date: null,
      warranty_end_date: null,
      warranty_notes: null,
    });
    setSelectedProduct(null);
    setAvailableSerials([]);
    setAvailableBatches([]);
  };

  const handleEditItem = async (index) => {
    const item = saleItems[index];
    setCurrentItem(item);
    setEditingItemIndex(index);
    setShowItemModal(true);

    let productId = item.product_id;
    if (item.product_data) {
      try {
        const pd =
          typeof item.product_data === "string"
            ? JSON.parse(item.product_data)
            : item.product_data;
        productId = pd.id || productId;
      } catch {
        /* intentional empty catch */
      }
    }

    if (productId) {
      setProductFetchLoading(true);
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(productId),
        );
        const product = res.data?.data;
        if (product) {
          setSelectedProduct(product);
          setAvailableBatches(product.batches || []);

          let serials = product.serial_numbers || [];
          if (product.is_serialized && saleItems) {
            const addedSerials = saleItems.reduce((acc, saleItem, idx) => {
              if (idx !== index && saleItem.product_id === product.id) {
                let itemSerials = [];
                if (
                  Array.isArray(saleItem.serial_numbers) &&
                  saleItem.serial_numbers.length > 0
                ) {
                  itemSerials = saleItem.serial_numbers;
                } else if (
                  typeof saleItem.serial_numbers === "string" &&
                  saleItem.serial_numbers !== "null"
                ) {
                  try {
                    itemSerials = JSON.parse(saleItem.serial_numbers || "[]");
                  } catch {
                    itemSerials = [];
                  }
                } else if (saleItem.inventory_serial?.serial_number) {
                  itemSerials = [saleItem.inventory_serial.serial_number];
                }

                if (Array.isArray(itemSerials)) {
                  acc.push(
                    ...itemSerials.map((sn) =>
                      typeof sn === "string" ? sn : sn.serial_number,
                    ),
                  );
                }
              }
              return acc;
            }, []);

            serials = serials.filter(
              (sn) => !addedSerials.includes(sn.serial_number),
            );
          }
          setAvailableSerials(serials);
        }
      } catch {
        /* intentional empty catch */
      } finally {
        setProductFetchLoading(false);
      }
    }
  };

  const handleRemoveItem = (index) => {
    const updatedItems = saleItems.filter((_, i) => i !== index);
    setSaleItems(updatedItems);
  };

  const handleOpenItemModal = () => {
    resetCurrentItem();
    setEditingItemIndex(null);
    setShowItemModal(true);
  };

  const validateSale = () => {
    if (!saleData.customer_id) {
      toast.error("Please select a customer");
      return false;
    }

    if (saleItems.length === 0) {
      toast.error("Please add at least one item to the sale");
      return false;
    }

    // When partially-paid, an initial payment amount is required
    if (saleData.payment_status === "partially-paid") {
      const totals = calculateSaleTotals();
      const pAmount = parseFloat(paymentAmount) || 0;
      const tAmount = parseFloat(totals.total) || 0;
      if (pAmount <= 0) {
        toast.error("Please enter an initial payment amount for a partially paid sale");
        return false;
      }
      if (pAmount >= tAmount && tAmount > 0) {
        toast.error("Payment amount equals or exceeds the total \u2014 use 'Completed' status instead");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateSale()) {
      return;
    }

    const totals = calculateSaleTotals();

    // Determine effective payment status
    let paymentStatus = saleData.payment_status || "pending";

    // completed/partially-paid are driven by the status dropdown; recordPayment is auto-set
    if (paymentStatus !== "completed" && paymentStatus !== "partially-paid") {
      if (recordPayment) {
        const pAmount = parseFloat(paymentAmount) || 0;
        const tAmount = parseFloat(totals.total) || 0;
        if (pAmount >= tAmount && tAmount > 0) {
          paymentStatus = "completed";
        } else if (pAmount > 0) {
          paymentStatus = "partially-paid";
        } else {
          paymentStatus = "pending";
        }
      } else {
        paymentStatus = saleData.payment_status || "pending";
      }
    }

    const salePayload = {
      ...saleData,
      payment_status: paymentStatus,
      subtotal: totals.subtotal,
      tax_amount: totals.totalTax,
      discount_amount: totals.totalDiscount + totals.saleDiscount,
      total: totals.total,
      item_count: saleItems.length,
    };

    try {
      const sale = await createSaleMutation.mutateAsync(salePayload);
      await saveSaleItemsMutation.mutateAsync(sale);

      // For 'completed', always use the live sale total (user may have set status before adding items)
      const effectivePaymentAmount =
        paymentStatus === "completed" ? totals.total : paymentAmount;

      eventBus.publish("sale:created", {
        sale,
        recordPayment,
        amount: effectivePaymentAmount,
        payment_method: saleData.payment_method,
      });

      generatePDFMutation.mutate(sale);
    } catch {
      /* intentional empty catch */
    }
  };

  const downloadPDF = () => {
    if (pdfBlobUrl && currentSaleData) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      const prefix = pdfType === "receipt" ? "receipt" : "invoice";
      link.download = `${prefix}-${currentSaleData.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const printPDF = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl);
      printWindow.print();
    }
  };

  const handleClosePDFModal = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
    }
    setPdfModalOpen(false);
    generatePDFMutation.reset();
    generateReceiptMutation.reset();
    createSaleMutation.reset();
    setPdfBlobUrl(null);
    setCurrentSaleData(null);

    // Invalidate here (not just in onSuccess) so the saleslist always
    // fetches fresh data after the mutation state has been reset above.
    queryClient.invalidateQueries({ queryKey: ["sales"] });
    navigate(all_routes.saleslist);
  };

  const productOptions = products.map((product) => ({
    value: product.id,
    label: `${product.name}${product.sku ? ` (SKU: ${product.sku})` : ""} - ${currencySymbol}${parseFloat(product.sell_price || 0).toFixed(2)}`,
    product: product,
  }));

  const customerOptions = customers.map((customer) => {
    let displayName = customer.full_name;

    if (customer.customer_type !== "individual" && customer.company) {
      displayName = customer.company;
    }

    const labels = [displayName, customer.phone].filter(Boolean);

    return {
      value: customer.id,
      label: labels.join(" - "),
    };
  });

  const totals = calculateSaleTotals();

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading title="New Sale" />
        <PageHeaderActions>
          <Button variant="outline" asChild>
            <Link to={all_routes.saleslist}>
              <ChevronUp className="mr-2 h-4 w-4" />
              Back to Sales
            </Link>
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              Sale Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer" className="flex items-center gap-1">
                  Customer <span className="text-destructive">*</span>
                </Label>
                <div className="flex gap-2">
                  <ReactSelect
                    id="customer"
                    className="flex-grow"
                    onInputChange={(value) => setCustomerSearchInput(value)}
                    filterOption={() => true}
                    options={customerOptions}
                    placeholder="Select Customer"
                    value={
                      saleData.customer_id
                        ? {
                          value: saleData.customer_id,
                          label:
                            customerLabels[String(saleData.customer_id)] ||
                            customerOptions.find(
                              (opt) => opt.value === saleData.customer_id,
                            )?.label ||
                            "Select Customer",
                        }
                        : null
                    }
                    onChange={(option) =>
                      setSaleData({
                        ...saleData,
                        customer_id: option?.value || null,
                      })
                    }
                    isLoading={isCustomersFetching}
                    isClearable
                  />
                  {hasPermission("customer create") && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setShowCustomerModal(true)}
                      title="Add New Customer"
                    >
                      <PlusCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_status">Payment Status</Label>
                <ReactSelect
                  id="payment_status"
                  options={paymentStatusOptions}
                  placeholder="Select Payment Status"
                  value={paymentStatusOptions.find(
                    (opt) => opt.value === saleData.payment_status,
                  )}
                  onChange={(option) => {
                    const newStatus = option?.value || null;
                    setSaleData({ ...saleData, payment_status: newStatus });

                    if (newStatus === "completed") {
                      // Auto-enable payment recording with full sale total
                      setRecordPayment(true);
                      setPaymentAmount(calculateSaleTotals().total);
                    } else if (newStatus === "partially-paid") {
                      // Auto-enable payment recording; user must enter a partial amount
                      setRecordPayment(true);
                      setPaymentAmount(0);
                    } else {
                      // For all other statuses, disable auto-record
                      setRecordPayment(false);
                      setPaymentAmount(0);
                    }
                  }}
                  isClearable
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_method">Payment Method</Label>
                <ReactSelect
                  id="payment_method"
                  options={paymentMethodOptions}
                  placeholder="Select Payment Method"
                  value={paymentMethodOptions.find(
                    (opt) => opt.value === saleData.payment_method,
                  )}
                  onChange={(option) =>
                    setSaleData({
                      ...saleData,
                      payment_method: option?.value || null,
                    })
                  }
                  isClearable
                />
              </div>

              {hasPermission("payment create") && (
                <div className="col-span-full bg-muted/50 p-4 rounded-lg border my-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="record_payment"
                      checked={recordPayment}
                      disabled={
                        saleData.payment_status === "completed" ||
                        saleData.payment_status === "partially-paid"
                      }
                      onChange={(e) => {
                        setRecordPayment(e.target.checked);
                        if (e.target.checked && paymentAmount === 0) {
                          setPaymentAmount(calculateSaleTotals().total);
                        } else if (!e.target.checked) {
                          setPaymentAmount(0);
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <Label
                      htmlFor="record_payment"
                      className="font-semibold cursor-pointer text-base"
                    >
                      Record Initial Payment
                      {saleData.payment_status === "completed" && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          (auto-enabled \u2014 payment status is Completed)
                        </span>
                      )}
                      {saleData.payment_status === "partially-paid" && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          (auto-enabled \u2014 payment status is Partially Paid)
                        </span>
                      )}
                    </Label>
                  </div>
                  {recordPayment && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="payment_amount" className="flex items-center gap-1">
                          Payment Amount
                          {saleData.payment_status === "partially-paid" && (
                            <span className="text-destructive">*</span>
                          )}
                        </Label>
                        <Input
                          id="payment_amount"
                          type="number"
                          placeholder={
                            saleData.payment_status === "partially-paid"
                              ? "Enter partial amount (required)"
                              : "Enter amount"
                          }
                          value={saleData.payment_status === "completed" ? totals.total : paymentAmount}
                          onChange={(e) =>
                            setPaymentAmount(parseFloat(e.target.value) || 0)
                          }
                          min="0.01"
                          step="0.01"
                          readOnly={saleData.payment_status === "completed"}
                        />
                        {saleData.payment_status === "completed" && (
                          <p className="text-xs text-muted-foreground">
                            Full sale total will be recorded as payment.
                          </p>
                        )}
                        {saleData.payment_status === "partially-paid" && (
                          <p className="text-xs text-muted-foreground">
                            Enter the initial partial amount paid (must be less than the total).
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="discount_type">Discount Type</Label>
                <ReactSelect
                  id="discount_type"
                  options={discountTypeOptions}
                  placeholder="Select Discount Type"
                  value={discountTypeOptions.find(
                    (opt) => opt.value === saleData.discount_type,
                  )}
                  onChange={(option) =>
                    setSaleData({
                      ...saleData,
                      discount_type: option?.value || null,
                    })
                  }
                  isClearable
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discount_value">Discount Value</Label>
                <Input
                  id="discount_value"
                  type="number"
                  placeholder="Enter discount value"
                  value={saleData.discount_value || 0}
                  onChange={(e) =>
                    setSaleData({
                      ...saleData,
                      discount_value: parseFloat(e.target.value) || 0,
                    })
                  }
                  min="0"
                  step="0.01"
                  disabled={!saleData.discount_type}
                />
              </div>

              <div className="col-span-full space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  placeholder="Enter notes"
                  value={saleData.notes || ""}
                  onChange={(e) =>
                    setSaleData({ ...saleData, notes: e.target.value })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="flex items-center gap-2">
              <ListIcon className="h-5 w-5 text-primary" />
              Sale Items
            </CardTitle>
            {hasPermission("sale item create") && (
              <Button type="button" size="sm" onClick={handleOpenItemModal}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Item
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <ShadcnTable>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Tax</TableHead>
                    <TableHead>Line Total</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody ref={dragContainerRef}>
                  {saleItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="text-center h-24 text-muted-foreground"
                      >
                        No items added yet. Add Items to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    saleItems.map((item, index) => {
                      let productName = "-";
                      try {
                        const productData = JSON.parse(item.product_data);
                        productName = productData.name || "-";
                      } catch (error) {
                        console.error("Failed to parse product data", error);
                      }

                      return (
                        <TableRow key={index} data-index={index}>
                          <TableCell className="text-center">
                            <div className="drag-handle cursor-move">
                              <Move
                                size={16}
                                className="text-muted-foreground"
                              />
                            </div>
                          </TableCell>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-foreground">
                                  {productName}
                                </span>
                              </div>
                              {item.notes && (
                                <span
                                  className="text-xs text-muted-foreground mt-0.5 line-clamp-2"
                                  title={item.notes}
                                >
                                  {item.notes}
                                </span>
                              )}
                              {item.inventory_batch_id && (
                                <span className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">
                                  <Badge
                                    variant="outline"
                                    className="px-1 py-0 text-[10px] h-3.5"
                                  >
                                    Batch
                                  </Badge>
                                  {JSON.parse(
                                    item.product_data || "{}",
                                  ).batches?.find(
                                    (b) => b.id === item.inventory_batch_id,
                                  )?.batch_number ||
                                    "ID: " + item.inventory_batch_id}
                                </span>
                              )}
                              {item.serial_numbers &&
                                item.serial_numbers.length > 0 && (
                                  <span className="text-[10px] text-muted-foreground italic flex flex-wrap gap-1 mt-0.5">
                                    <Badge
                                      variant="outline"
                                      className="px-1 py-0 text-[10px] h-3.5 bg-primary/5"
                                    >
                                      SN
                                    </Badge>
                                    {item.serial_numbers
                                      .map((sn) =>
                                        typeof sn === "string"
                                          ? sn
                                          : sn.serial_number,
                                      )
                                      .join(", ")}
                                  </span>
                                )}
                            </div>
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            {currencySymbol}
                            {parseFloat(item.unit_price || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {currencySymbol}
                            {parseFloat(item.discount_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {currencySymbol}
                            {parseFloat(item.tax_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {currencySymbol}
                            {parseFloat(item.line_total || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {hasPermission("sale item update") && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-primary"
                                  onClick={() => handleEditItem(index)}
                                >
                                  <Edit size={14} />
                                </Button>
                              )}
                              {hasPermission("sale item delete") && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => handleRemoveItem(index)}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </ShadcnTable>
            </div>

            {saleItems.length > 0 && (
              <div className="flex justify-end mt-6">
                <div className="w-full max-w-md">
                  <Card>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span className="font-medium">
                          {currencySymbol}
                          {totals.subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Item Discounts:
                        </span>
                        <span className="text-destructive font-medium">
                          - {currencySymbol}
                          {totals.totalDiscount.toFixed(2)}
                        </span>
                      </div>
                      {totals.saleDiscount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Sale Discount:
                          </span>
                          <span className="text-destructive font-medium">
                            - {currencySymbol}
                            {totals.saleDiscount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Tax:</span>
                        <span className="font-medium">
                          {currencySymbol}
                          {totals.totalTax.toFixed(2)}
                        </span>
                      </div>
                      <div className="pt-2 border-t flex justify-between items-center">
                        <span className="font-bold text-lg">Total:</span>
                        <span className="font-bold text-lg text-primary">
                          {currencySymbol}
                          {totals.total.toFixed(2)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 mt-8">
          {hasPermission("sale create") && (
            <Button
              onClick={handleSubmit}
              disabled={createSaleMutation.isPending}
              size="lg"
              className="px-8"
            >
              {createSaleMutation.isPending ? (
                <>
                  <Spinner size={16} className="mr-2" />
                  Creating Sale...
                </>
              ) : (
                "Create Sale"
              )}
            </Button>
          )}
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate(all_routes.saleslist)}
          >
            Cancel
          </Button>
        </div>
      </div>
      <Dialog
        open={showItemModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowItemModal(false);
            resetCurrentItem();
            setEditingItemIndex(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>
              {editingItemIndex !== null ? "Edit Item" : "Add Item"}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[80vh]">
            <div className="grid grid-cols-1 gap-4 py-4 px-6">
              <div className="col-span-full space-y-2">
                <Label className="flex items-center gap-1">
                  Product <span className="text-destructive">*</span>
                </Label>
                <div className="flex gap-2">
                  <ReactSelect
                    className="flex-grow"
                    options={productOptions}
                    placeholder="Type to search products (min 3 characters)..."
                    value={
                      selectedProduct
                        ? {
                          value: selectedProduct.id,
                          label: `${selectedProduct.name || ""
                            } - ${currencySymbol}${parseFloat(
                              selectedProduct.sell_price || 0,
                            ).toFixed(2)}`,
                        }
                        : null
                    }
                    onChange={(option) => handleProductSelect(option)}
                    onInputChange={handleProductSearch}
                    filterOption={() => true}
                    isLoading={productSearchLoading || productFetchLoading}
                    isDisabled={productFetchLoading}
                    isClearable
                    maxMenuHeight={250}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => {
                      setShowItemModal(false);
                      setShowProductModal(true);
                    }}
                    title="Add New Product"
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {selectedProduct && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Quantity <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="number"
                      placeholder="Enter quantity"
                      value={currentItem.quantity}
                      onChange={(e) => {
                        const quantity = parseInt(e.target.value) || 1;
                        const updatedItem = { ...currentItem, quantity };
                        const taxAmount = calculateTaxAmount(updatedItem);
                        setCurrentItem({
                          ...updatedItem,
                          tax_amount: taxAmount,
                        });
                      }}
                      min="1"
                      disabled={selectedProduct?.is_serialized}
                      title={
                        selectedProduct?.is_serialized
                          ? "Quantity is determined by the number of serials selected"
                          : ""
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Unit Price <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="number"
                      placeholder="Enter unit price"
                      value={currentItem.unit_price}
                      onChange={(e) => {
                        const unit_price = parseFloat(e.target.value) || 0;
                        const updatedItem = { ...currentItem, unit_price };
                        const taxAmount = calculateTaxAmount(updatedItem);
                        setCurrentItem({
                          ...updatedItem,
                          tax_amount: taxAmount,
                        });
                      }}
                      min="0"
                      step="0.01"
                    />
                    <div className="flex gap-2">
                      {(selectedProduct?.sell_price_min ?? 0) > 0 && (
                        <Badge
                          variant="outline"
                          className="text-xs font-normal"
                        >
                          Min: {currencySymbol}
                          {parseFloat(selectedProduct.sell_price_min).toFixed(
                            2,
                          )}
                        </Badge>
                      )}
                      {(selectedProduct?.sell_price_max ?? 0) > 0 && (
                        <Badge
                          variant="outline"
                          className="text-xs font-normal"
                        >
                          Max: {currencySymbol}
                          {parseFloat(selectedProduct.sell_price_max).toFixed(
                            2,
                          )}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Discount Type</Label>
                    <ReactSelect
                      options={discountTypeOptions}
                      placeholder="Select Discount Type"
                      value={discountTypeOptions.find(
                        (opt) => opt.value === currentItem.discount_type,
                      )}
                      onChange={(option) => {
                        const updatedItem = {
                          ...currentItem,
                          discount_type: option?.value || null,
                          discount_value: 0,
                        };
                        const taxAmount = calculateTaxAmount(updatedItem);
                        setCurrentItem({
                          ...updatedItem,
                          tax_amount: taxAmount,
                        });
                      }}
                      isClearable
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Discount Value</Label>
                    <Input
                      type="number"
                      placeholder="Enter discount value"
                      value={currentItem.discount_value || 0}
                      onChange={(e) => {
                        const discount_value = parseFloat(e.target.value) || 0;
                        const updatedItem = {
                          ...currentItem,
                          discount_value,
                        };
                        const taxAmount = calculateTaxAmount(updatedItem);
                        setCurrentItem({
                          ...updatedItem,
                          tax_amount: taxAmount,
                        });
                      }}
                      min="0"
                      step="0.01"
                      disabled={!currentItem.discount_type}
                    />
                  </div>

                  {selectedProduct &&
                    (selectedProduct.tracking_type === "standard" ||
                      selectedProduct.is_serialized) && (
                      <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 mt-2">
                        {selectedProduct.tracking_type === "standard" && (
                          <div className="space-y-2 col-span-full">
                            <Label>
                              Select Batch{" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <ReactSelect
                              options={availableBatches.map((b) => ({
                                value: b.id,
                                label: `${b.batch_number} - Exp: ${b.expiry_date || "N/A"
                                  } (Stock: ${b.quantity})`,
                              }))}
                              value={availableBatches
                                .map((b) => ({
                                  value: b.id,
                                  label: `${b.batch_number} - Exp: ${b.expiry_date || "N/A"
                                    } (Stock: ${b.quantity})`,
                                }))
                                .find(
                                  (opt) =>
                                    opt.value ===
                                    currentItem.inventory_batch_id,
                                )}
                              onChange={(opt) =>
                                setCurrentItem({
                                  ...currentItem,
                                  inventory_batch_id: opt?.value,
                                })
                              }
                              placeholder="Select a batch"
                              maxMenuHeight={200}
                            />
                          </div>
                        )}

                        {selectedProduct.is_serialized && (
                          <div className="space-y-2 col-span-full">
                            <Label>
                              Select Serial Numbers (Selected:{" "}
                              {currentItem.serial_numbers?.length || 0}){" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <ReactSelect
                              isMulti
                              options={availableSerials.map((s) => ({
                                value: s.id,
                                label: s.serial_number,
                                serialNumber: s.serial_number,
                              }))}
                              value={(currentItem.serial_numbers || []).map(
                                (sn) => ({
                                  value: typeof sn === "string" ? sn : sn.id,
                                  label:
                                    typeof sn === "string"
                                      ? sn
                                      : sn.serial_number,
                                  serialNumber:
                                    typeof sn === "string"
                                      ? sn
                                      : sn.serial_number,
                                }),
                              )}
                              onChange={(opts) => {
                                const selectedSerials = opts
                                  ? opts.map((o) => ({
                                    id: o.value,
                                    serial_number: o.serialNumber,
                                  }))
                                  : [];
                                setCurrentItem({
                                  ...currentItem,
                                  serial_numbers: selectedSerials,
                                  quantity:
                                    selectedSerials.length > 0
                                      ? selectedSerials.length
                                      : 1,
                                });
                              }}
                              placeholder="Select serial numbers"
                              maxMenuHeight={250}
                            />
                            <p className="text-xs text-muted-foreground">
                              For serialized products, quantity is determined by
                              the number of serials selected.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                  <div className="space-y-2">
                    <Label className="font-bold text-sm text-muted-foreground flex items-center gap-2">
                      Sales Tax
                      {selectedProduct?.is_taxable ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] h-4 px-1 text-green-600 border-green-200"
                        >
                          Taxable
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-[10px] h-4 px-1 text-muted-foreground border-muted"
                        >
                          Non-taxable
                        </Badge>
                      )}
                    </Label>
                    <Input
                      type="text"
                      className="bg-muted/50 font-medium"
                      value={
                        selectedProduct?.is_taxable
                          ? `Automated (${selectedProduct?.tax_method || "Standard"
                          })`
                          : "No Tax Applied"
                      }
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Tax Amount</Label>
                    <Input
                      type="text"
                      className="bg-muted font-mono"
                      value={`${currencySymbol}${calculateItemTotal(
                        currentItem,
                      ).taxAmount.toFixed(2)}`}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 mt-2">
                    <div className="col-span-full">
                      <Label className="font-bold text-sm text-muted-foreground flex items-center gap-2">
                        Warranty Information
                      </Label>
                    </div>
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={
                          currentItem.warranty_start_date
                            ? currentItem.warranty_start_date.split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            warranty_start_date: e.target.value
                              ? new Date(e.target.value).toISOString()
                              : null,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={
                          currentItem.warranty_end_date
                            ? currentItem.warranty_end_date.split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            warranty_end_date: e.target.value
                              ? new Date(e.target.value).toISOString()
                              : null,
                          })
                        }
                      />
                    </div>
                    <div className="col-span-full space-y-2">
                      <Label>Warranty Notes</Label>
                      <Textarea
                        rows={2}
                        placeholder="Enter warranty notes"
                        value={currentItem.warranty_notes || ""}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            warranty_notes: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="col-span-full space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      placeholder="Enter notes"
                      value={currentItem.notes || ""}
                      onChange={(e) =>
                        setCurrentItem({
                          ...currentItem,
                          notes: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-span-full mt-2">
                    <Card className="bg-muted/50">
                      <CardContent className="p-4 space-y-2">
                        {(() => {
                          const { discountAmount, taxAmount, lineTotal } =
                            calculateItemTotal(currentItem);
                          const displayTotal = round2(lineTotal + taxAmount);
                          return (
                            <>
                              <div className="flex justify-between text-sm">
                                <span>Base Price:</span>
                                <span>
                                  {currencySymbol}
                                  {(
                                    (parseFloat(currentItem.unit_price) || 0) *
                                    (parseInt(currentItem.quantity) || 1)
                                  ).toFixed(2)}
                                </span>
                              </div>
                              {discountAmount > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span>Discount:</span>
                                  <span className="text-destructive">
                                    - {currencySymbol}
                                    {discountAmount.toFixed(2)}
                                  </span>
                                </div>
                              )}
                              {taxAmount > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span>Tax:</span>
                                  <span>
                                    {currencySymbol}
                                    {taxAmount.toFixed(2)}
                                  </span>
                                </div>
                              )}
                              <div className="pt-2 border-t flex justify-between items-center">
                                <span className="font-bold">Line Total:</span>
                                <span className="font-bold text-lg">
                                  {currencySymbol}
                                  {displayTotal.toFixed(2)}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter className="px-6 py-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowItemModal(false);
                resetCurrentItem();
                setEditingItemIndex(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddItem} disabled={!selectedProduct}>
              {editingItemIndex !== null ? "Update Item" : "Add to Sale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={pdfModalOpen} onOpenChange={handleClosePDFModal}>
        <DialogContent className="p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice PDF
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-4">
            {currentSaleData && (
              <Card className="mb-4 bg-muted/50 border-none">
                <CardContent className="p-4">
                  <p className="font-semibold">
                    Sale #{currentSaleData.invoice_number}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Total: {currencySymbol}
                    {parseFloat(currentSaleData.total || 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
            )}

            {generatePDFMutation.isPending && (
              <div className="flex items-center justify-center p-4 gap-3 text-sm">
                <Spinner size={16} />
                Generating PDF invoice...
              </div>
            )}

            {generatePDFMutation.isSuccess &&
              !generatePDFMutation.isPending && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm border border-green-100">
                    <CheckCircle2 className="h-4 w-4" />
                    PDF invoice generated successfully!
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={downloadPDF} className="w-full gap-2">
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      onClick={printPDF}
                      className="w-full gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      Print
                    </Button>
                    {hasPermission("report sales receipt view") && (
                      <Button
                        variant="outline"
                        className="col-span-2 gap-2"
                        onClick={() =>
                          generateReceiptMutation.mutate(currentSaleData)
                        }
                        disabled={generateReceiptMutation.isPending}
                      >
                        {generateReceiptMutation.isPending ? (
                          <Spinner size={16} className="text-current" />
                        ) : (
                          <Printer className="h-4 w-4" />
                        )}
                        Print Thermal Receipt
                      </Button>
                    )}
                    {currentSaleData?.customer?.email &&
                      currentSaleData.customer.email !== "" &&
                      currentSaleData.customer.email !== "-" && (
                        <Button
                          variant="secondary"
                          className="col-span-2 gap-2"
                          onClick={() =>
                            sendEmailMutation.mutate(currentSaleData)
                          }
                          disabled={sendEmailMutation.isPending}
                        >
                          {sendEmailMutation.isPending ? (
                            <Spinner size={16} className="text-current" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                          Send to {currentSaleData.customer.email}
                        </Button>
                      )}
                  </div>
                </div>
              )}

            {generatePDFMutation.isError && !generatePDFMutation.isPending && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  <AlertCircle className="h-4 w-4" />
                  Generation failed. Please try again.
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    generatePDFMutation.reset();
                    generatePDFMutation.mutate(currentSaleData);
                  }}
                >
                  Retry A4 Invoice
                </Button>
              </div>
            )}

            {!generatePDFMutation.isPending &&
              !generatePDFMutation.isSuccess &&
              !generatePDFMutation.isError && (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    className="w-full col-span-2"
                    onClick={() => generatePDFMutation.mutate(currentSaleData)}
                  >
                    Generate A4 Invoice
                  </Button>
                </div>
              )}
          </div>
        </DialogContent>
      </Dialog>
      <ProductModal
        open={showProductModal}
        onOpenChange={setShowProductModal}
        onSuccess={handleProductCreated}
      />
      <CustomerModal
        open={showCustomerModal}
        onOpenChange={setShowCustomerModal}
        onSuccess={(newCustomer) => {
          let displayName =
            newCustomer.full_name ||
            `${newCustomer.first_name} ${newCustomer.last_name}`;
          if (
            newCustomer.customer_type !== "individual" &&
            newCustomer.company
          ) {
            displayName = newCustomer.company;
          }
          const labels = [displayName, newCustomer.phone].filter(Boolean);
          setCustomerLabels((prev) => ({
            ...prev,
            [String(newCustomer.id)]: labels.join(" - "),
          }));
          setSaleData((prev) => ({
            ...prev,
            customer_id: newCustomer.id,
          }));
        }}
      />
    </PageContainer>
  );
};

export default NewSale;
